import React, { PureComponent } from 'react';
import { injectIntl } from 'react-intl';
import { format } from 'date-fns';
import { RaisedButton, CircularProgress } from 'material-ui';
import { groupBy, uniqWith, isEqual, cloneDeep, sortBy } from 'lodash';
import ReportRow, { ROW_TYPE } from './ReportRow';
import { v4 as uuidv4 } from 'uuid';
import { CSR, CSM } from '../../../../constants/UserType';
import './MarketCenterReportTable.css';
import { ISO_DATE_FORMAT } from '../../../../constants/constants';

class MarketCenterReportTable extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      tableData: null
    };

    this.createRowData = this.createRowData.bind(this);
    this.getMCLevelData = this.getMCLevelData.bind(this);
    this.getDistrictLevelData = this.getDistrictLevelData.bind(this);
    this.getRouteLevelData = this.getRouteLevelData.bind(this);
    this.normalizeData = this.normalizeData.bind(this);
    this.onMcClick = this.onMcClick.bind(this);
    this.onDistrictClick = this.onDistrictClick.bind(this);
  }

  componentDidMount() {
    this.setState({ tableData: this.normalizeData(this.props.reportData) });
  }

  getMCLevelData(mcData) {
    const groupedByMC = groupBy(mcData, (d) => d.branchCode);
    return Object.entries(groupedByMC).map((mc) =>
      this.createRowData(ROW_TYPE.MC, mc[0], '*', '*', mc[1], (shouldExpand) =>
        this.onMcClick(mc[0], shouldExpand)
      )
    );
  }

  getDistrictLevelData(mcData) {
    const uniqueBranchDistricts = uniqWith(
      mcData.map((data) => ({
        branchCode: data.branchCode,
        districtCode: data.districtCode
      })),
      isEqual
    );

    return uniqueBranchDistricts.map((data) =>
      this.createRowData(
        ROW_TYPE.DISTRICT,
        data.branchCode,
        data.districtCode,
        '*',
        mcData.filter(
          (d) =>
            d.branchCode === data.branchCode &&
            d.districtCode === data.districtCode
        ),
        (shouldExpand) =>
          this.onDistrictClick(data.branchCode, data.districtCode, shouldExpand)
      )
    );
  }

  getRouteLevelData(mcData) {
    const uniqueBranchDistrictRoutes = uniqWith(
      mcData.map((data) => ({
        branchCode: data.branchCode,
        districtCode: data.districtCode,
        routeNumber: data.routeNumber
      })),
      isEqual
    );

    return uniqueBranchDistrictRoutes.map((data) =>
      this.createRowData(
        ROW_TYPE.ROUTE,
        data.branchCode,
        data.districtCode,
        data.routeNumber,
        mcData.filter(
          (d) =>
            d.branchCode === data.branchCode &&
            d.districtCode === data.districtCode &&
            d.routeNumber === data.routeNumber
        )
      )
    );
  }

  normalizeByDate(reportData) {
    reportData.forEach((d) => {
      if (!!d.lastModifiedDate) {
        d.lastModifiedDate = format(
          new Date(d.lastModifiedDate),
          ISO_DATE_FORMAT
        );

        if (
          d.lastModifiedDate < this.props.startDate ||
          d.lastModifiedDate > this.props.endDate
        ) {
          d.proposedCount = 0;
          d.completedCount = 0;
          d.withinMadCount = 0;
        }
      }
    });
  }

  normalizeData(reportData) {
    this.normalizeByDate(reportData);
    const mcLevelData = this.getMCLevelData(reportData);
    const distictLevelData = this.getDistrictLevelData(reportData);
    const routeLevelData = this.getRouteLevelData(reportData);
    const tableData = [...mcLevelData, ...distictLevelData, ...routeLevelData];
    return sortBy(tableData, ['branchCode', 'districtCode', 'routeNumber']);
  }
  createRowData(
    rowType,
    branchCode,
    districtCode,
    routeNumber,
    renewals,
    onClick,
    expand = false
  ) {
    return {
      id: uuidv4(),
      expand,
      onClick,
      rowType,
      branchCode,
      districtCode,
      routeNumber,
      totalProposed: renewals.reduce((sum, r) => sum + r.proposedCount, 0),
      totalCompleted: renewals.reduce((sum, r) => sum + r.completedCount, 0),
      totalWithinMad: renewals.reduce((sum, r) => sum + r.withinMadCount, 0),
      totalCsrProposed: renewals
        .filter((r) => !!r.role && r.role.toUpperCase() === CSR.name)
        .reduce((sum, r) => sum + r.proposedCount, 0),
      totalCsrCompleted: renewals
        .filter((r) => !!r.role && r.role.toUpperCase() === CSR.name)
        .reduce((sum, r) => sum + r.completedCount, 0),
      totalCsrWithinMad: renewals
        .filter((r) => !!r.role && r.role.toUpperCase() === CSR.name)
        .reduce((sum, r) => sum + r.withinMadCount, 0),
      totalDmProposed: renewals
        .filter((r) => !!r.role && r.role.toUpperCase() === CSM.name)
        .reduce((sum, r) => sum + r.proposedCount, 0),
      totalDmCompleted: renewals
        .filter((r) => !!r.role && r.role.toUpperCase() === CSM.name)
        .reduce((sum, r) => sum + r.completedCount, 0),
      totalDmWithinMad: renewals
        .filter((r) => !!r.role && r.role.toUpperCase() === CSM.name)
        .reduce((sum, r) => sum + r.withinMadCount, 0),
      totalOtherProposed: renewals
        .filter(
          (r) =>
            !!r.role &&
            r.role.toUpperCase() !== CSR.name &&
            r.role.toUpperCase() !== CSM.name
        )
        .reduce((sum, r) => sum + r.proposedCount, 0),
      totalOtherCompleted: renewals
        .filter(
          (r) =>
            !!r.role &&
            r.role.toUpperCase() !== CSR.name &&
            r.role.toUpperCase() !== CSM.name
        )
        .reduce((sum, r) => sum + r.completedCount, 0),
      totalOtherWithinMad: renewals
        .filter(
          (r) =>
            !!r.role &&
            r.role.toUpperCase() !== CSR.name &&
            r.role.toUpperCase() !== CSM.name
        )
        .reduce((sum, r) => sum + r.withinMadCount, 0)
    };
  }

  onMcClick(branchCode, shouldExpand) {
    const tableData = cloneDeep(this.state.tableData);

    tableData.find(
      (data) => data.rowType === ROW_TYPE.MC && data.branchCode === branchCode
    ).expand = shouldExpand;

    this.setState({
      tableData
    });
  }

  onDistrictClick(branchCode, districtCode, shouldExpand) {
    const tableData = cloneDeep(this.state.tableData);

    tableData.find(
      (data) =>
        data.rowType === ROW_TYPE.DISTRICT &&
        data.branchCode === branchCode &&
        data.districtCode === districtCode
    ).expand = shouldExpand;

    this.setState({
      tableData
    });
  }

  getRows() {
    const mcRows = this.state.tableData.filter(
      (data) => data.rowType === ROW_TYPE.MC
    );

    return mcRows.map((data) => (
      <>
        <ReportRow {...data} />
        {data.expand ? this.getDistrictRows(data.branchCode) : null}
      </>
    ));
  }

  getDistrictRows(branchCode) {
    const districtRows = this.state.tableData.filter(
      (data) =>
        data.rowType === ROW_TYPE.DISTRICT && data.branchCode === branchCode
    );

    return districtRows.map((data) => (
      <>
        <ReportRow {...data} />
        {data.expand
          ? this.getRoutetRows(data.branchCode, data.districtCode)
          : null}
      </>
    ));
  }

  getRoutetRows(branchCode, districtCode) {
    const routeRows = this.state.tableData.filter(
      (data) =>
        data.rowType === ROW_TYPE.ROUTE &&
        data.branchCode === branchCode &&
        data.districtCode === districtCode
    );

    return routeRows.map((data) => <ReportRow {...data} />);
  }

  render() {
    return !!this.state.tableData ? (
      <div>
        <div className="mc-report-table-scroll-bar">
          <table className="mc-report-table">
            <thead>
              <tr key="report-header" className="mc-report-header">
                <th colSpan="3"></th>
                <th colSpan="3">
                  {this.props.intl.formatMessage({ id: 'total_initiated' })}
                </th>
                <th colSpan="3">
                  {this.props.intl.formatMessage({ id: 'dm_initiated' })}
                </th>
                <th colSpan="3">
                  {this.props.intl.formatMessage({ id: 'rsr_initiated' })}
                </th>
                <th colSpan="3">
                  {this.props.intl.formatMessage({ id: 'other_initiated' })}
                </th>
              </tr>
              <tr key="report-sub-header" className="mc-report-sub-header">
                <th>
                  {this.props.intl.formatMessage({ id: 'market_center' })}
                </th>
                <th>
                  {this.props.intl.formatMessage({ id: 'district_code' })}
                </th>
                <th>{this.props.intl.formatMessage({ id: 'route_number' })}</th>
                <th>{this.props.intl.formatMessage({ id: 'proposed' })}</th>
                <th>{this.props.intl.formatMessage({ id: 'completed' })}</th>
                <th>{this.props.intl.formatMessage({ id: 'within_mad' })}</th>
                <th>{this.props.intl.formatMessage({ id: 'proposed' })}</th>
                <th>{this.props.intl.formatMessage({ id: 'completed' })}</th>
                <th>{this.props.intl.formatMessage({ id: 'within_mad' })}</th>
                <th>{this.props.intl.formatMessage({ id: 'proposed' })}</th>
                <th>{this.props.intl.formatMessage({ id: 'completed' })}</th>
                <th>{this.props.intl.formatMessage({ id: 'within_mad' })}</th>
                <th>{this.props.intl.formatMessage({ id: 'proposed' })}</th>
                <th>{this.props.intl.formatMessage({ id: 'completed' })}</th>
                <th>{this.props.intl.formatMessage({ id: 'within_mad' })}</th>
              </tr>
            </thead>
            <tbody>{this.getRows()}</tbody>
          </table>
        </div>
        <RaisedButton
          label={this.props.intl.formatMessage({ id: 'export_mc_report' })}
          primary
          onClick={() => this.props.exportToCSV(this.state.tableData)}
          style={{ marginTop: '20px' }}
        />
      </div>
    ) : (
      <div style={{ width: '100%', display: 'flex', margin: '10px' }}>
        <CircularProgress style={{ margin: 'auto' }} />
      </div>
    );
  }
}

export default injectIntl(MarketCenterReportTable);
